import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { appContext } from "../../../../AppContext";
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, HelperText, IGridData, IGridRowsRequest, IGridRowsResponse, IPageNavSection, ISelectItem, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, Switch, TextField, TickSymbol } from "@iconics/components";
import { Validator } from '@iconics/core';
import { Attribute, Entity } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import Helper from "../../../../Common/Helper";
import { StorageTypeEnum, HistoryStorageMethodEnum, BuiltInType } from '../../../../Providers.FadxPlatformApiClientV2/FadxPlatformApiClientV2'
import { SerendieSymbol } from "@serendie/symbols";
import { Guid, TypeHelper } from "@iconics/core";

class EditEntity extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get packageId(): string { return this.props.params.packageId ?? ''; }
    private get entityId(): number | undefined { return TypeHelper.toNumber(this.props.params.entityId ?? '') ?? undefined; }

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            canDelete: false,
            canCreate: false,
            canUpdate: false,
            canSetupAttribute: false,
            sections: [],
            name: '',
            nameError: '',
            displayName: '',
            storageTypeOptions: [],
            storageTypeError: '',
            historyStorageMethodOptions: [],
            historyStorageMethodError: '',
            isAuditLogDisabled: false,
            showAllAttributes: false,
            attributesGridData: { rows: [] },
            attributesGridRefreshToken: Guid.new().toString(),
            attributeError: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const { labels } = appContext();
        this.pageTitle = labels.Platform_Entity_PageTitle;
        this.backUrl = `/admin/packages/${this.packageId}/edit`;

        this.setState({
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.Entities.Delete'),
            canCreate: appContext().localStorageProvider.hasRight('DATAMODEL.Entities.Create'),
            canUpdate: appContext().localStorageProvider.hasRight('DATAMODEL.Entities.Update'),
            canSetupAttribute: appContext().localStorageProvider.hasRight('ADMINPORTAL.Attributes.Setup'),
        });

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        this.validator
            .addInput('storage-type', () => this.state.storageTypeId ?? '', i => this.setState({ storageTypeError: i }))
            .isRequired();

        this.validator
            .addInput('history-storage-method', () => this.state.historyStorageMethodId ?? '', i => this.setState({ historyStorageMethodError: i }))
            .isRequired();

        await this.loading(async () =>
        {
            this.loadStorageTypeOptions();
            this.loadHistoryStorageMethodOptions();
            await this.loadDetails();
        });
    }

    private loadStorageTypeOptions(): void
    {
        const storageTypes = appContext().staticDataProvider.getStorageTypes();
        const storageTypeOptions = storageTypes.map(i => ({ value: i.id, label: i.name } satisfies ISelectItem));
        this.setState({ storageTypeOptions: storageTypeOptions });
    }

    private loadHistoryStorageMethodOptions(): void
    {
        const storageMethods = appContext().staticDataProvider.getHistoryStorageMethods();
        const storageMethodOptions = storageMethods.map(i => ({ value: i.id, label: i.name } satisfies ISelectItem));
        this.setState({ historyStorageMethodOptions: storageMethodOptions });
    }

    private async loadDetails(): Promise<void>
    {
        const { entityId } = this;
        if (!entityId)
        {
            return;
        }

        const entityData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.get<Entity>({
            select: Entity,
            tenancy: appContext().tenantId,
            id: entityId.toString(),
        });

        this.setState({
            name: entityData.Name,
            displayName: entityData.DisplayName ?? '',
            storageTypeId: entityData.StorageType as StorageTypeEnum,
            historyStorageMethodId: entityData.HistoryStorageMethod as HistoryStorageMethodEnum,
            isAuditLogDisabled: TypeHelper.toBoolean(entityData.AuditLogDisabled) ?? false,
            primaryKeyFieldId: entityData.EventPrimaryKeyField_ID,
            timestampFieldId: entityData.EventTimeField_ID,
        });
    }

    private async handleDelete(): Promise<void>
    {
        const { entityId } = this;
        if (!entityId || !this.state.canDelete)
        {
            return;
        }

        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.delete({
                id: entityId.toString(),
                tenancy: appContext().tenantId,
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async handleCancel(): Promise<void>
    {
        this.props.navigate(this.backUrl);
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll() || (!this.entityId && !this.state.canCreate) || (this.entityId && !this.state.canUpdate))
        {
            return;
        }

        await this.loading(async () => 
        {
            const { entityId } = this;
            if (!entityId)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.post({
                    body: {
                        Name: this.state.name,
                        DisplayName: this.state.displayName,
                        Package_ID: this.packageId,
                        StorageType: this.state.storageTypeId || undefined,
                        HistoryStorageMethod: this.state.historyStorageMethodId || undefined,
                        AuditLogDisabled: (this.state.isAuditLogDisabled ? 1 : 0),
                    },
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.patch({
                    body: {
                        Name: this.state.name,
                        DisplayName: this.state.displayName,
                        StorageType: this.state.storageTypeId || undefined,
                        HistoryStorageMethod: this.state.historyStorageMethodId || undefined,
                        AuditLogDisabled: (this.state.isAuditLogDisabled ? 1 : 0),
                    },
                    id: entityId.toString(),
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate(this.backUrl);
        });
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();
        const isNew = !this.entityId;

        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer
                        spacing={5}
                    >
                        <GridItem>
                            {this.renderDetails()}
                        </GridItem>
                        {
                            !isNew &&
                            <>
                                <GridItem>
                                    {this.renderAttributes()}
                                </GridItem>
                            </>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.validator.isValid || (isNew && !this.state.canCreate) || (!isNew && !this.state.canUpdate)}
                                onClick={() => this.handleSave()}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
            </LayoutContainer>
        );
    }

    private renderDetails(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <Card
                title={labels.Platform_Entity_DetailsTitle}
                subTitle={labels.Platform_Entity_DetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={labels.Entity_Name}
                            placeholder={labels.Entity_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                            required
                            invalid={!!this.state.nameError}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={labels.Entity_DisplayName}
                            placeholder={labels.Entity_DisplayName}
                            value={this.state.displayName}
                            onChange={e => this.setState({ displayName: e.target.value })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={labels.Entity_StorageType}
                            placeholder={labels.Entity_StorageType}
                            onSelect={e => this.setState({ storageTypeId: e.value as StorageTypeEnum }, () => this.validator.validate('storage-type'))}
                            items={this.state.storageTypeOptions}
                            value={this.state.storageTypeId ? [this.state.storageTypeId] : undefined}
                            required
                            invalid={!!this.state.storageTypeError}
                            invalidMessage={this.state.storageTypeError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={labels.Entity_HistoryStorageMethod}
                            placeholder={labels.Entity_HistoryStorageMethod}
                            onSelect={e => this.setState({ historyStorageMethodId: e.value as HistoryStorageMethodEnum }, () => this.validator.validate('history-storage-method'))}
                            items={this.state.historyStorageMethodOptions}
                            value={this.state.historyStorageMethodId ? [this.state.historyStorageMethodId] : undefined}
                            required
                            invalid={!!this.state.historyStorageMethodError}
                            invalidMessage={this.state.historyStorageMethodError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Switch
                            label={labels.Entity_AuditLogDisabled}
                            checked={this.state.isAuditLogDisabled}
                            onCheckedChange={e => this.setState({ isAuditLogDisabled: e.checked })}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private async handleAttributeRowsRequest(request: IGridRowsRequest<IAttribute>): Promise<IGridRowsResponse<IAttribute>>
    {
        if (this.state.showAllAttributes)
        {
            const attributesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.get<PagedResponse<Attribute[]>>({
                select: Attribute,
                tenancy: appContext().tenantId,
                filter: `Package_ID eq '${this.packageId}'`,
                top: request.take,
                skipToken: request.skipToken,
                orderBy: DataGrid.toODataOrderBy(request.sorter),
            });

            const addedAttributesData = await this.getAddedAttributes();
            const addedAttributesIds = addedAttributesData.map(i => i.Id);

            const attributes = attributesData.value.map(i => ({
                id: i.Id,
                name: i.Name,
                builtInType: i.BuiltInType,
                isArray: i.IsArray ?? false,
                isRequired: i.IsMandatory ?? false,
                isAddedToEntity: addedAttributesIds.includes(i.Id),
            } satisfies IAttribute));

            return {
                rows: attributes,
                nextSkipToken: attributesData.skipToken,
            };
        }
        else
        {
            if (!this.entityId) return { rows: [], nextSkipToken: '' };
            const addedAttributesData = await this.getAddedAttributes();

            const addedAttributes = addedAttributesData.map(i => ({
                id: i.Id,
                name: i.Name,
                builtInType: i.BuiltInType,
                isArray: i.IsArray ?? false,
                isRequired: i.IsMandatory ?? false,
                isAddedToEntity: true,
            } satisfies IAttribute));

            const sortedAttributes = DataGrid.sort(addedAttributes, request.sorter);

            return {
                rows: sortedAttributes,
                nextSkipToken: '',
            };
        }
    }

    private addedAttributes?: Attribute[];
    private async getAddedAttributes(): Promise<Attribute[]>
    {
        const { entityId } = this;
        if (!entityId) return [];

        if (!this.addedAttributes)
        {
            const addedAttributesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.attributes.get<PagedResponse<Attribute[]>>({
                tenancy: appContext().tenantId,
                id: entityId,
            });

            this.addedAttributes = addedAttributesData.value;
        }
        return this.addedAttributes;
    }

    private async saveAttributes(options?: {
        primaryKeyFieldId?: number,
        timestampFieldId?: number,
        attributeIdsToAdd?: number[],
        attributeIdsToRemove?: number[],
    }): Promise<void>
    {
        const { labels } = appContext();
        const primaryKeyFieldId = (options?.primaryKeyFieldId ?? this.state.primaryKeyFieldId ?? this.state.pendingPrimaryKeyFieldId);
        const timestampFieldId = (options?.timestampFieldId ?? this.state.timestampFieldId ?? this.state.pendingTimestampFieldId);
        const idsToAdd = (options?.attributeIdsToAdd ?? []);
        const idsToRemove = (options?.attributeIdsToRemove ?? []);
        const timestampRow = this.state.attributesGridData.rows.find(i => i.id == timestampFieldId);
        const errors: string[] = [];
        let primaryKeyIsValid = true;
        let timestampIsValid = true;
        this.setState({ attributeError: '' });

        const { entityId } = this;
        if (!entityId) return;

        if (!primaryKeyFieldId)
        {
            errors.push(labels.Platform_Entity_Attribute_PrimaryKeyIsRequired);
            primaryKeyIsValid = false;
        }
        if (!timestampFieldId)
        {
            errors.push(labels.Platform_Entity_Attribute_TimestampIsRequired);
            timestampIsValid = false;
        }
        if (timestampFieldId && timestampRow?.builtInType != 'DateTime')
        {
            errors.push(labels.Platform_Entity_Attribute_TimestampMustBeDateTime);
            timestampIsValid = false;
        }
        if (primaryKeyFieldId && timestampFieldId && primaryKeyFieldId == timestampFieldId)
        {
            errors.push(labels.Platform_Entity_Attribute_PrimaryKeyAndTimestampMustBeDifferent);
            primaryKeyIsValid = false;
            timestampIsValid = false;
        }
        if (idsToRemove.includes(primaryKeyFieldId ?? 0) || idsToRemove.includes(timestampFieldId ?? 0))
        {
            errors.push(labels.Platform_Entity_Attribute_CannotRemovePrimaryKeyOrTimestamp);
        }

        if (primaryKeyIsValid)
        {
            this.setState({ pendingPrimaryKeyFieldId: primaryKeyFieldId });
        }
        if (timestampIsValid)
        {
            this.setState({ pendingTimestampFieldId: timestampFieldId });
        }
        if (errors.length > 0)
        {
            this.setState({ attributeError: errors.join(' ') });
            return;
        }

        await this.loading(async () =>
        {
            const attributes = await this.getAddedAttributes();

            const attributeIds = [primaryKeyFieldId as number, timestampFieldId as number, ...idsToAdd, ...attributes.map(i => i.Id)]
                .filter(i => !idsToRemove.includes(i))
                .distinct();

            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.byId.attributes.set.patch({
                id: entityId.toString(),
                tenancy: appContext().tenantId,
                body: {
                    EventPrimaryKeyField_ID: primaryKeyFieldId,
                    EventTimeField_ID: timestampFieldId,
                    EventFieldIds: attributeIds,
                    IndexedColumnIds: attributeIds,
                    InputAliases: attributeIds.map(i => ''),
                    EventFieldFunctions: attributeIds.map(i => 'None'),
                    EventFieldExpressions: attributeIds.map(i => ''),
                },
            });

            this.addedAttributes = undefined;
            await this.loadDetails();

            this.setState({
                attributesGridRefreshToken: Guid.new().toString(),
                primaryKeyFieldId: primaryKeyFieldId,
                pendingPrimaryKeyFieldId: undefined,
                timestampFieldId: timestampFieldId,
                pendingTimestampFieldId: undefined,
            });
        });
    }

    private get checkedAttributeIds(): number[]
    {
        return (this.state.attributesGridData.checkedIds ?? [])
            .map(i => TypeHelper.toNumber(i))
            .filter((i): i is number => !!i);
    }

    private renderAttributes(): JSX.Element
    {
        const { labels } = appContext();
        const noneChecked = this.checkedAttributeIds.length == 0;
        const oneChecked = this.checkedAttributeIds.length == 1;
        const primaryKeyAdded = !!this.state.primaryKeyFieldId;
        const timestampAdded = !!this.state.timestampFieldId;
        const { showAllAttributes } = this.state;

        return (
            <Card
                title={labels.Platform_Entity_AttributeListTitle}
                subTitle={labels.Platform_Entity_AttributeListSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='close' />}
                                    onClick={() => this.saveAttributes({ attributeIdsToRemove: this.checkedAttributeIds })}
                                    disabled={!this.state.canSetupAttribute || noneChecked || !primaryKeyAdded || !timestampAdded}
                                >
                                    {labels.Platform_Entity_RemoveAttributes}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='check' />}
                                    onClick={() => this.saveAttributes({ attributeIdsToAdd: this.checkedAttributeIds })}
                                    disabled={!this.state.canSetupAttribute || noneChecked || !primaryKeyAdded || !timestampAdded}
                                >
                                    {labels.Platform_Entity_AddAttributes}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='key' />}
                                    onClick={() => this.saveAttributes({ primaryKeyFieldId: this.checkedAttributeIds[0] })}
                                    disabled={!this.state.canSetupAttribute || !oneChecked}
                                >
                                    {labels.Platform_Entity_SetPrimaryKey}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='clock' />}
                                    onClick={() => this.saveAttributes({ timestampFieldId: this.checkedAttributeIds[0] })}
                                    disabled={!this.state.canSetupAttribute || !oneChecked}
                                >
                                    {labels.Platform_Entity_SetTimestamp}
                                </Button>
                                {
                                    !showAllAttributes &&
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='eye' />}
                                        onClick={() => this.setStateAsync({ showAllAttributes: true, attributesGridRefreshToken: Guid.new().toString() })}
                                    >
                                        {labels.Platform_Entity_ShowAllAttributes}
                                    </Button>
                                }
                                {
                                    showAllAttributes &&
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='eye-hidden' />}
                                        onClick={() => this.setStateAsync({ showAllAttributes: false, attributesGridRefreshToken: Guid.new().toString() })}
                                    >
                                        {labels.Platform_Entity_HideAllAttributes}
                                    </Button>
                                }
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    {
                        this.state.attributeError &&
                        <GridItem>
                            <HelperText error={true}>{this.state.attributeError}</HelperText>
                        </GridItem>
                    }
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: 'Type',
                                    title: '',
                                    getCellValue: i => this.renderAttributeType(i.id),
                                    sortable: false,
                                    columnWidth: 5,
                                },
                                {
                                    columnId: (showAllAttributes ? Helper.nameOf<Attribute>('Name') : Helper.nameOf<IAttribute>('name')),
                                    title: labels.Attribute_Name,
                                    getCellValue: i => i.name,
                                    sortable: true,
                                    columnWidth: 25,
                                },
                                {
                                    columnId: (showAllAttributes ? Helper.nameOf<Attribute>('BuiltInType') : Helper.nameOf<IAttribute>('builtInType')),
                                    title: labels.Attribute_BuiltInType,
                                    getCellValue: i => i.builtInType,
                                    sortable: true,
                                    columnWidth: 25,
                                },
                                {
                                    columnId: (showAllAttributes ? Helper.nameOf<Attribute>('IsArray') : Helper.nameOf<IAttribute>('isArray')),
                                    title: labels.Attribute_IsArray,
                                    getCellValue: i => <TickSymbol value={i.isArray} />,
                                    sortable: true,
                                    columnWidth: 15,
                                },
                                {
                                    columnId: (showAllAttributes ? Helper.nameOf<Attribute>('IsMandatory') : Helper.nameOf<IAttribute>('isRequired')),
                                    title: labels.Attribute_IsRequired,
                                    getCellValue: i => <TickSymbol value={i.isRequired} />,
                                    sortable: true,
                                    columnWidth: 15,
                                },
                                {
                                    columnId: Helper.nameOf<IAttribute>('isAddedToEntity'),
                                    title: labels.Attribute_IsAddedToEntity,
                                    getCellValue: i => <TickSymbol value={i.isAddedToEntity} />,
                                    sortable: false,
                                    columnWidth: 15,
                                },
                            ]}
                            data={this.state.attributesGridData}
                            onChange={i => this.setState({ attributesGridData: i, attributeError: '' })}
                            rowId={i => i.id.toString()}
                            onRowsRequest={i => this.handleAttributeRowsRequest(i)}
                            showCheckBoxes={true}
                            refreshToken={this.state.attributesGridRefreshToken}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private renderAttributeType(attributeId: number): JSX.Element
    {
        if (attributeId == this.state.primaryKeyFieldId)
        {
            return <SerendieSymbol name='key' />
        }
        else if (attributeId == this.state.pendingPrimaryKeyFieldId)
        {
            return <SerendieSymbol name='key' style={{ color: 'var(--colors-sd-system-color-impression-negative)' }} />
        }
        else if (attributeId == this.state.timestampFieldId)
        {
            return <SerendieSymbol name='clock' />
        }
        else if (attributeId == this.state.pendingTimestampFieldId)
        {
            return <SerendieSymbol name='clock' style={{ color: 'var(--colors-sd-system-color-impression-negative)' }} />
        }
        else
        {
            return <></>
        }
    }
}

export default withRouting(EditEntity);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    packageId?: string;
    entityId?: string;
}

export interface IState
{
    canDelete: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canSetupAttribute: boolean;
    sections: IPageNavSection[];
    name: string;
    nameError: string;
    displayName: string;
    storageTypeId?: StorageTypeEnum;
    storageTypeOptions: ISelectItem[];
    storageTypeError: string;
    historyStorageMethodId?: HistoryStorageMethodEnum;
    historyStorageMethodOptions: ISelectItem[];
    historyStorageMethodError: string;
    isAuditLogDisabled: boolean;
    showAllAttributes: boolean;
    attributesGridData: IGridData<IAttribute>;
    attributesGridRefreshToken: string;
    attributeError: string;
    primaryKeyFieldId?: number;
    pendingPrimaryKeyFieldId?: number;
    timestampFieldId?: number;
    pendingTimestampFieldId?: number;
}

export interface IAttribute
{
    id: number;
    name: string;
    builtInType: BuiltInType | '';
    isArray: boolean;
    isRequired: boolean;
    isAddedToEntity: boolean;
}
